import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setReadOnlySession } from "@/lib/api";
import { isReadOnlyUser } from "@/lib/roles";
import { startReadOnlyMode, stopReadOnlyMode } from "@/lib/readOnlyMode";
import { applyColorThemePrefs } from "@/hooks/useColorTheme";
import { setAppTheme } from "@/hooks/useAppTheme";
import { resetBranding, loadBranding } from "@/hooks/useBranding";
import { resetNotifications } from "@/lib/notifications";

const AuthContext = createContext(null);

/** The account's saved appearance wins over the per-device cache once we know who's logged in. */
const applyAccountTheme = (u) => {
  if (!u) return;
  applyColorThemePrefs({ color_theme: u.color_theme, theme_mode: u.theme_mode, light_theme: u.light_theme });
  if (u.app_theme) setAppTheme(u.app_theme);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("bf_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        applyAccountTheme(res.data);
      })
      .catch(() => localStorage.removeItem("bf_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("bf_token", res.data.token);
    setUser(res.data.user);
    applyAccountTheme(res.data.user);
    // the /auth page loaded the public branding shape — re-fetch as the signed-in organisation
    // so signed-in-only fields (invoice & payment details) are available to the app
    loadBranding(true);
    return res.data.user;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const res = await api.post("/auth/register", { name, email, password });
    if (res.data.pending) {
      // account awaits admin approval — no session issued
      return res.data;
    }
    localStorage.setItem("bf_token", res.data.token);
    setUser(res.data.user);
    loadBranding(true);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("bf_token");
    resetNotifications();
    resetBranding();
    setUser(null);
    window.location.href = "/auth";
  }, []);

  /** Landlord (super-admin) only: work inside another organisation as its admin, or come home.
   *  The API issues a fresh session bound to that organisation; a full reload drops every
   *  cached list so nothing from the previous tenant lingers on screen. */
  const switchOrg = useCallback(async (orgId) => {
    const res = orgId ? await api.post(`/superadmin/switch/${orgId}`) : await api.post("/superadmin/switch/home");
    localStorage.setItem("bf_token", res.data.token);
    resetBranding();
    window.location.href = "/";
    return res.data;
  }, []);

  // Merge a partial patch into the current user (e.g. onboarding progress)
  const updateUser = useCallback((patch) => {
    setUser((u) => (u ? { ...u, ...patch } : u));
  }, []);

  // Phase 191 — "Read Only — view all": switch the API guard + the global view-only engine with the session
  const isReadOnly = isReadOnlyUser(user);
  useEffect(() => {
    setReadOnlySession(isReadOnly);
    if (isReadOnly) startReadOnlyMode();
    else stopReadOnlyMode();
    return () => stopReadOnlyMode();
  }, [isReadOnly]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, switchOrg, isReadOnly }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
